import React, { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useAuth } from '@/api/AuthContext';
import { ClipboardList, Users, Package, User, LogOut, Menu, X, Truck, ArrowUpFromLine, ArrowDownToLine, Warehouse, ClipboardCheck, Calendar, Landmark, BookOpen, Wallet, ScanLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import AdminSidebar, { useAdminSidebarCollapsed, ADMIN_SECTIONS } from '@/Components/nav/AdminSidebar';
import { useShopifySync } from '@/lib/useShopifySync';

const agentPages = ['AgentTasks', 'AgentLeads', 'AgentOrders', 'AgentCatalog', 'AgentProfile', 'AgentReport'];
const adminPages = ['AdminDashboard', 'AdminLeads', 'AdminTasks', 'AdminOrders', 'AdminReports', 'AdminAgents', 'AdminInbox', 'AdminLeadHunter', 'AdminProducts', 'AdminStockMovements', 'AdminSuppliers', 'AdminAgentPerformance', 'AdminPdfSettings', 'AdminShopifyOrders', 'AdminEwkKategorien', 'AdminIntegrationen', 'AdminKundenportal', 'AdminEinstellungen'];
const warehousePages = ['WarehouseWarenausgang', 'WarehouseWareneingang', 'WarehouseDispo', 'WarehouseDashboard', 'WarehouseInventur', 'WarehouseSuppliers', 'AgentProfile'];
const shippingPages = ['ShippingDashboard', 'AgentProfile'];
const buchhaltungPages = ['BuchhaltungRechnungen', 'BuchhaltungDashboard', 'BuchhaltungJournal', 'BuchhaltungKonten', 'BuchhaltungBerichte', 'BuchhaltungBank', 'BuchhaltungAusgaben', 'BuchhaltungWiederkehrend', 'BuchhaltungAnlagen', 'BuchhaltungAbschluss', 'BuchhaltungMahnungen', 'BuchhaltungGutschriften'];

const warehouseNavItems = [
  { name: 'WarehouseWarenausgang', label: 'Ausgang', icon: ArrowUpFromLine },
  { name: 'WarehouseWareneingang', label: 'Eingang', icon: ArrowDownToLine },
  { name: 'WarehouseDispo', label: 'Dispo', icon: Calendar },
  { name: 'WarehouseDashboard', label: 'Depo', icon: Warehouse },
  { name: 'WarehouseInventur', label: 'Inventur', icon: ClipboardCheck },
  { name: 'WarehouseSuppliers', label: 'Lieferant', icon: Truck },
];

const shippingNavItems = [
  { name: 'ShippingDashboard', label: 'Versand (Sevkiyat)', icon: Truck },
  { name: 'AgentProfile', label: 'Profil', icon: User },
];

// Buchhaltung rolü için alt navigasyon (mobil). Admin sidebar kullanır.
const buchhaltungNavItems = [
  { name: 'BuchhaltungDashboard', label: 'Finanzen', icon: Landmark },
  { name: 'BuchhaltungAusgaben', label: 'Ausgaben', icon: ScanLine },
  { name: 'BuchhaltungBank', label: 'Bank', icon: Wallet },
  { name: 'BuchhaltungJournal', label: 'Journal', icon: BookOpen },
  { name: 'AgentProfile', label: 'Profil', icon: User },
];

const agentNavItems = [
  { name: 'AgentTasks', label: 'Aufgaben', icon: ClipboardList },
  { name: 'AgentOrders', label: 'Aufträge', icon: Package },
  { name: 'AgentLeads', label: 'Kontakte', icon: Users },
  { name: 'AgentProfile', label: 'Profil', icon: User },
];

// Admin masaüstü sidebar + mobil drawer artık tek kaynaktan (ADMIN_SECTIONS,
// AdminSidebar.jsx) beslenir. Eski adminNavItems/adminAllNavItems kaldırıldı.

export default function Layout({ children, currentPageName }) {
  const { user, logout, canAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useAdminSidebarCollapsed();

  // Otomatik Shopify sipariş senkronu — admin & depo kullanıcıları için.
  // Yeni siparişler orders tablosuna 'Neu' statüsüyle düşer → otomatik depo emri.
  useShopifySync({ enabled: ['admin', 'warehouse'].includes(user?.role) });

  // Determine current page name from path
  const getPageNameFromPath = (path) => {
    if (path.includes('/agent/tasks')) return 'AgentTasks';
    if (path.includes('/agent/orders')) return 'AgentOrders';
    if (path.includes('/agent/leads')) return 'AgentLeads';
    if (path.includes('/agent/catalog')) return 'AgentCatalog';
    if (path.includes('/agent/profile')) return 'AgentProfile';
    if (path.includes('/agent/report')) return 'AgentReport';

    if (path.includes('/warehouse/warenausgang')) return 'WarehouseWarenausgang';
    if (path.includes('/warehouse/wareneingang')) return 'WarehouseWareneingang';
    if (path.includes('/warehouse/dispo')) return 'WarehouseDispo';
    if (path.includes('/warehouse/depo')) return 'WarehouseDashboard';
    if (path.includes('/warehouse/inventur')) return 'WarehouseInventur';
    if (path.includes('/warehouse/suppliers')) return 'WarehouseSuppliers';
    if (path.includes('/warehouse/profile')) return 'AgentProfile';

    if (path.includes('/shipping')) return 'ShippingDashboard';

    if (path.includes('/buchhaltung/rechnungen')) return 'BuchhaltungRechnungen';
    if (path.includes('/buchhaltung/mahnungen')) return 'BuchhaltungMahnungen';
    if (path.includes('/buchhaltung/gutschriften')) return 'BuchhaltungGutschriften';
    if (path.includes('/buchhaltung/finanzen')) return 'BuchhaltungDashboard';
    if (path.includes('/buchhaltung/journal')) return 'BuchhaltungJournal';
    if (path.includes('/buchhaltung/konten')) return 'BuchhaltungKonten';
    if (path.includes('/buchhaltung/berichte')) return 'BuchhaltungBerichte';
    if (path.includes('/buchhaltung/bank')) return 'BuchhaltungBank';
    if (path.includes('/buchhaltung/ausgaben')) return 'BuchhaltungAusgaben';
    if (path.includes('/buchhaltung/wiederkehrend')) return 'BuchhaltungWiederkehrend';
    if (path.includes('/buchhaltung/anlagen')) return 'BuchhaltungAnlagen';
    if (path.includes('/buchhaltung/abschluss')) return 'BuchhaltungAbschluss';
    if (path.includes('/buchhaltung/profile')) return 'AgentProfile';

    if (path.includes('/admin/dashboard')) return 'AdminDashboard';
    if (path.includes('/admin/leads')) return 'AdminLeads';
    if (path.includes('/admin/tasks')) return 'AdminTasks';
    if (path.includes('/admin/orders')) return 'AdminOrders';
    if (path.includes('/admin/products')) return 'AdminProducts';
    if (path.includes('/admin/stock-movements')) return 'AdminStockMovements';
    if (path.includes('/admin/suppliers')) return 'AdminSuppliers';
    if (path.includes('/admin/agents')) return 'AdminAgents';
    if (path.includes('/admin/reports')) return 'AdminReports';
    if (path.includes('/admin/inbox')) return 'AdminInbox';
    if (path.includes('/admin/lead-hunter')) return 'AdminLeadHunter';
    if (path.includes('/admin/performance')) return 'AdminAgentPerformance';
    if (path.includes('/admin/pdf-settings')) return 'AdminPdfSettings';
    if (path.includes('/admin/ewk-kategorien')) return 'AdminEwkKategorien';
    if (path.includes('/admin/integrationen')) return 'AdminIntegrationen';
    if (path.includes('/admin/kundenportal')) return 'AdminKundenportal';
    if (path.includes('/admin/einstellungen')) return 'AdminEinstellungen';
    if (path.includes('/admin/shopify-orders')) return 'AdminShopifyOrders';

    return '';
  };

  const activePageName = currentPageName || getPageNameFromPath(location.pathname);
  const isAgentPage = agentPages.includes(activePageName);
  const isAdminPage = adminPages.includes(activePageName);
  const isWarehousePage = warehousePages.includes(activePageName);
  const isShippingPage = shippingPages.includes(activePageName);
  const isBuchhaltungPage = buchhaltungPages.includes(activePageName)
    || location.pathname.startsWith('/buchhaltung');

  // Admin, Lager/Versand ekranlarını da sidebar üzerinden gezebildiği için
  // admin rolündeyken alt (agent/depo) nav'ı gösterme — sidebar yeterli.
  const showAgentNav = (isAgentPage || isWarehousePage || isShippingPage || isBuchhaltungPage)
    && user && user?.role !== 'admin';
  // Admin kullanıcısına her sayfada üst bar görünsün ki MegaTopNav'dan
  // başka rol alanlarına geçip geri dönebilsin.
  const showAdminNav = (isAdminPage && user) || user?.role === 'admin';
  // Sidebar: admin rolündeyse Lager/Versand ekranlarında da görünür
  // (tek merkezi navigasyon). Admin dışı rollerde asla.
  const showAdminSidebar = user?.role === 'admin'
    && (isAdminPage || isWarehousePage || isShippingPage || isBuchhaltungPage);

  // Choose which nav items to show in the bottom nav.
  // ÖNCELİK: kullanıcı ROLÜ. (Bugfix: AgentProfile hem agent- hem
  // warehousePages'te olduğundan, agent profilinde depo menüsü çıkıyor
  // ve tıklanan her öğe rol uyuşmazlığıyla login'e atıyordu.)
  const rawNavItems =
    user?.role === 'warehouse' ? warehouseNavItems :
    user?.role === 'shipping' ? shippingNavItems :
    user?.role === 'buchhaltung' ? buchhaltungNavItems :
    user?.role === 'agent' ? agentNavItems :
    // admin diğer alanlarda gezerken URL alanına göre
    location.pathname.startsWith('/warehouse') ? warehouseNavItems :
    location.pathname.startsWith('/shipping') ? shippingNavItems :
    location.pathname.startsWith('/buchhaltung') ? buchhaltungNavItems :
    agentNavItems;
  // Permission filtreleme: kullanıcının erişebileceği ekranlar
  const currentNavItems = rawNavItems.filter(item => canAccess(item.name));

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">

      {/* Admin Top Bar */}
      {showAdminNav && (
        <header className="sticky top-0 z-50 bg-brand-950 text-white border-b border-white/10">
          <div className="w-full px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile hamburger menu */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <Menu className="w-5 h-5 text-white/70" />
              </button>
              <span className="text-lg font-bold tracking-tight">IM</span>
              <span className="text-xs text-white/50 hidden sm:inline">Admin Panel</span>
            </div>
            <div className="flex items-center gap-2">
              {user?.role === 'admin' && !isAdminPage && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">
                  {isWarehousePage ? 'Lager-Ansicht' : isShippingPage ? 'Versand-Ansicht' : isAgentPage ? 'Agent-Ansicht' : 'Sonstige'}
                </span>
              )}
              <NotificationCenter />
              <span className="text-xs text-white/60 hidden sm:inline">{user?.name || user?.email}</span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-xs text-white/50 hover:text-red-400 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Abmelden</span>
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Admin Sidebar - Desktop only, admin pages only */}
      {showAdminSidebar && (
        <AdminSidebar
          activePageName={activePageName}
          collapsed={sidebarCollapsed}
          onToggle={setSidebarCollapsed}
        />
      )}

      {/* Admin Mobile Drawer Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <div className="bg-brand-950 text-white p-4 pb-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-lg font-bold">InMotion</span>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            <p className="text-xs text-white/50">{user?.name || user?.email}</p>
          </div>
          <nav className="py-2 overflow-y-auto max-h-[calc(100vh-160px)]">
            {ADMIN_SECTIONS.map(section => {
              const items = section.items.filter(item => canAccess(item.name));
              if (items.length === 0) return null;
              return (
                <div key={section.label} className="mb-2">
                  <p className="px-4 mt-2 mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {section.label}
                  </p>
                  {items.map(item => {
                    const isActive = activePageName === item.name;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={createPageUrl(item.name)}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors min-h-[48px] active:bg-slate-100',
                          isActive
                            ? 'bg-brand-50 text-brand-900 border-r-2 border-brand-900'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        )}
                      >
                        <Icon className="w-5 h-5 shrink-0" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </nav>
          <div className="border-t p-4">
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 text-sm text-red-600 hover:text-red-700 w-full min-h-[44px]"
            >
              <LogOut className="w-5 h-5" />
              Abmelden
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <main className={cn(
        "min-h-screen transition-[margin] duration-200 ease-out",
        showAgentNav && 'pb-24', // Add padding for bottom nav
        showAdminSidebar && (sidebarCollapsed ? 'md:ml-16' : 'md:ml-60')
      )}>
        {children || <Outlet />}
      </main>

      {/* Agent Bottom Navigation - Mobile First Design */}
      {showAgentNav && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 safe-area-inset-bottom shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="max-w-md mx-auto px-2">
            <div className="flex justify-around items-center h-16">
              {currentNavItems.map(item => {
                const isActive = activePageName === item.name;
                const Icon = item.icon;

                return (
                  <Link
                    key={item.name}
                    to={createPageUrl(item.name)}
                    className={cn(
                      'flex flex-col items-center justify-center flex-1 h-full transition-all duration-200',
                      isActive ? 'text-brand-900 translate-y-[-2px]' : 'text-slate-400 hover:text-slate-600'
                    )}
                  >
                    <div className={cn(
                      "p-1.5 rounded-xl transition-all",
                      isActive && "bg-brand-50"
                    )}>
                      <Icon className={cn('w-6 h-6', isActive && 'fill-current')} strokeWidth={isActive ? 2.5 : 2} />
                    </div>
                    <span className={cn(
                      "text-[10px] font-medium mt-0.5",
                      isActive ? "text-brand-900" : "text-slate-400"
                    )}>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
              {/* Logout Button in Agent Nav */}
              <button
                onClick={handleLogout}
                className="flex flex-col items-center justify-center flex-1 h-full text-slate-400 hover:text-red-500 transition-all duration-200"
              >
                <div className="p-1.5 rounded-xl">
                  <LogOut className="w-6 h-6" strokeWidth={2} />
                </div>
                <span className="text-[10px] font-medium mt-0.5">Logout</span>
              </button>
            </div>
          </div>
        </nav>
      )}
    </div>
  );
}
